import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { clientsApi } from '@/features/clients/api/clients.api';
import { mapAccountCompany } from '@/features/clients/utils/companyDisplay';
import type { Company } from '@/features/clients/interfaces/company.interface';

export type { Company } from '@/features/clients/interfaces/company.interface';

export type CompanySelection = 'all' | number;

interface CompanyContextValue {
  companies: Company[];
  selectedCompanyId: CompanySelection;
  setSelectedCompanyId: (id: CompanySelection) => void;
  selectedCompany: Company | null;
  isAllSelected: boolean;
  isLoading: boolean;
  error: Error | null;
}

const CompanyContext = createContext<CompanyContextValue | undefined>(undefined);

const STORAGE_KEY = 'tls.selectedCompany';

function readStoredSelection(): CompanySelection {
  if (typeof window === 'undefined') return 'all';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return 'all';
  if (stored === 'all') return 'all';
  const numeric = Number(stored);
  return Number.isFinite(numeric) ? numeric : 'all';
}

function writeStoredSelection(value: CompanySelection) {
  localStorage.setItem(STORAGE_KEY, value === 'all' ? 'all' : String(value));
}

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [selectedCompanyId, setSelectedCompanyIdState] =
    useState<CompanySelection>(() => readStoredSelection());

  const {
    data: companies = [],
    isLoading,
    error,
  } = useQuery<Company[]>({
    queryKey: ['account-companies', user?.id ?? null],
    queryFn: async () => {
      const raw = await clientsApi.getAccountCompanies();
      return raw.map(mapAccountCompany);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    writeStoredSelection(selectedCompanyId);
  }, [selectedCompanyId]);

  // Dacă firma selectată nu mai există în lista venită de la API, revine la "all"
  useEffect(() => {
    if (selectedCompanyId === 'all' || companies.length === 0) return;
    const stillExists = companies.some((c) => c.id === selectedCompanyId);
    if (!stillExists) setSelectedCompanyIdState('all');
  }, [companies, selectedCompanyId]);

  const setSelectedCompanyId = useCallback((id: CompanySelection) => {
    setSelectedCompanyIdState(id);
  }, []);

  const value = useMemo<CompanyContextValue>(() => {
    const selectedCompany =
      selectedCompanyId === 'all'
        ? null
        : companies.find((c) => c.id === selectedCompanyId) || null;
    return {
      companies,
      selectedCompanyId,
      setSelectedCompanyId,
      selectedCompany,
      isAllSelected: selectedCompanyId === 'all',
      isLoading,
      error: (error as Error) || null,
    };
  }, [companies, selectedCompanyId, setSelectedCompanyId, isLoading, error]);

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}
